// @section 1 Campaigns / Touch — (master x AZ-date x T1-T5+) cross-tab
// Canonical read shape from /refresh-campaign-metrics Step 2. Per-day per-touch
// breakdown of sent / opens / open% / replies / engaged / redirect / GA4
// sessions + leads. 14-day default window. Anomaly highlight when sent >= 50
// and open% < 10%. Engaged cell highlighted when engaged > 0.

import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  getCampaignTouchRollup,
  CampaignsUnconfigured,
  type CampaignTouchRollup,
  type CampaignTouchRow,
} from '../../api';

// @section 2 Main
export default function Touch() {
  const [data, setData] = useState<CampaignTouchRollup | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [unconfigured, setUnconfigured] = useState(false);
  const [windowDays, setWindowDays] = useState<number>(14);

  // @subsection 2.1 Data load
  useEffect(() => {
    setData(null);
    setErr(null);
    getCampaignTouchRollup(windowDays)
      .then(setData)
      .catch((e: unknown) => {
        if (e instanceof CampaignsUnconfigured) {
          setUnconfigured(true);
          return;
        }
        setErr(String(e));
      });
  }, [windowDays]);

  // @subsection 2.2 Derived — totals, dated section breaks
  const rows = data?.rows ?? null;
  const totals = useMemo(() => {
    if (!rows) return null;
    return rows.reduce(
      (acc, r) => {
        acc.sent += r.sent;
        acc.opens += r.opens;
        acc.replies += r.replies;
        acc.engaged += r.engaged;
        acc.redirect += r.redirect;
        acc.ga4_sessions += r.ga4_sessions;
        return acc;
      },
      { sent: 0, opens: 0, replies: 0, engaged: 0, redirect: 0, ga4_sessions: 0 },
    );
  }, [rows]);

  return (
    <div className="md:-mx-10 md:-my-8">
      <section className="bg-ink-950 text-paper-100 min-h-screen font-sans">
        <div className="px-4 py-5 md:px-10 md:py-7 max-w-[1400px] mx-auto">

          {/* @subsection 2.3 Breadcrumb */}
          <div className="flex items-center justify-between text-xs mb-6">
            <div className="text-muted">
              <Link to="/campaigns" className="text-paper-100 hover:underline">Atlas</Link>
              <span className="opacity-40"> / </span>
              <Link to="/campaigns" className="text-paper-100/80 hover:underline">Campaigns</Link>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100">Touch</span>
            </div>
            <div className="flex items-center gap-3 text-[11px]">
              <span className="text-muted">Window:</span>
              {[7, 14, 30].map((d) => (
                <button
                  key={d}
                  onClick={() => setWindowDays(d)}
                  className={
                    'px-2 py-0.5 rounded ' +
                    (windowDays === d
                      ? 'bg-accent-400/20 text-accent-400 ring-1 ring-accent-400/40'
                      : 'text-muted hover:text-paper-100')
                  }
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>

          {/* @subsection 2.4 Title + sub-nav */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-[3px] text-muted mb-2">
                Master x AZ-date x Touch
              </div>
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-paper-100 mb-2">
                Touch rollup
              </h1>
              <p className="text-base text-muted">
                {totals
                  ? `${fmt(totals.sent)} sent · ${fmt(totals.opens)} opens · ${fmt(totals.replies)} replies · ${fmt(totals.engaged)} engaged · last ${windowDays}d`
                  : unconfigured
                    ? 'Campaign engagement pipeline not configured'
                    : 'Loading touch rollup…'}
              </p>
            </div>
            <div className="text-xs text-muted flex gap-3 mt-2">
              <Link to="/campaigns" className="hover:text-paper-100">Overview</Link>
              <Link to="/campaigns/funnel" className="hover:text-paper-100">Funnel</Link>
              <Link to="/campaigns/trend" className="hover:text-paper-100">Trend</Link>
              <Link to="/campaigns/leads" className="hover:text-paper-100">Leads</Link>
              <Link to="/campaigns/replies" className="hover:text-paper-100">Replies</Link>
              <span className="text-paper-100 font-semibold">Touch</span>
            </div>
          </div>

          {/* @subsection 2.5 Unconfigured banner */}
          {unconfigured && (
            <div className="rounded-xl border-2 border-watch/40 bg-ink-900 p-5 text-sm text-watch ring-1 ring-watch/10 mb-6">
              <div className="font-semibold mb-1">Campaigns pipeline isn't configured</div>
              <div className="text-muted">
                Backend returned <code className="text-watch">503 campaigns_unconfigured</code>.
                Once the campaign-engagement pipeline has produced its first refresh, this page populates.
              </div>
            </div>
          )}

          {/* @subsection 2.6 Errors / loading */}
          {err && (
            <div className="rounded-xl border border-stall/40 bg-ink-900 p-4 text-sm text-stall mb-6">
              {err}
            </div>
          )}
          {!rows && !err && !unconfigured && (
            <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-8 text-center text-muted text-sm">
              Loading…
            </div>
          )}

          {/* @subsection 2.7 The table */}
          {rows && rows.length === 0 && !unconfigured && (
            <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-8 text-center text-muted text-sm">
              No sends in the window.
            </div>
          )}

          {rows && rows.length > 0 && (
            <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-muted text-left">
                    <th className="py-2 pr-3">AZ Date</th>
                    <th className="py-2 pr-3">Master</th>
                    <th className="py-2 pr-3">Touch</th>
                    <th className="py-2 px-3 text-right">Sent</th>
                    <th className="py-2 px-3 text-right">Opens</th>
                    <th className="py-2 px-3 text-right">Open %</th>
                    <th className="py-2 px-3 text-right">Replies</th>
                    <th className="py-2 px-3 text-right">Engaged</th>
                    <th className="py-2 px-3 text-right">Redirect</th>
                    <th className="py-2 px-3 text-right">GA4 sess</th>
                    <th className="py-2 pl-3 text-right">GA4 leads</th>
                  </tr>
                </thead>
                <tbody>
                  {renderRows(rows)}
                </tbody>
              </table>
            </div>
          )}

          {/* @subsection 2.8 Footer */}
          <div className="mt-8 text-xs text-muted leading-relaxed">
            <p>
              Reads <code className="text-paper-100/70">analytics.outreach_send_facts</code> + <code className="text-paper-100/70">analytics.outreach_ga4_sessions</code>,
              joined to <code className="text-paper-100/70">adapter_instantly.email_messages</code> for engaged/redirect classes.
              Engaged + redirect are positive-signal classes (human_engaged, human_redirect).
              GA4 columns are scanner-filtered (traffic_class='human') and attributed to the latest preceding send.
            </p>
            <p className="mt-2">
              Surfaces intentionally excluded: bounces (list-quality signal, not creative);
              Instantly clicks (broken for our motion — see GA4 sessions instead).
              For interactive interpretation or to force a fresh upstream refresh, run <code className="text-paper-100/70">/refresh-campaign-metrics</code> from Claude Code.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

// @section 3 Render helpers
function renderRows(rows: CampaignTouchRow[]) {
  // Insert section break (border-top) when az_date changes.
  let lastDate: string | null = null;
  const out: JSX.Element[] = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const dateChanged = r.az_date !== lastDate;
    lastDate = r.az_date;
    const sent = r.sent ?? 0;
    const opens = r.opens ?? 0;
    const openPct = r.open_pct;
    const anomaly = sent >= 50 && opens / Math.max(sent, 1) < 0.1;
    const engagedHot = (r.engaged ?? 0) > 0;
    out.push(
      <tr
        key={`${r.master}|${r.az_date}|${r.touch}|${i}`}
        className={dateChanged ? 'border-t-2 border-ink-700' : 'border-t border-ink-800'}
      >
        <td className="py-1.5 pr-3 text-paper-100/80">{r.az_date}</td>
        <td className="py-1.5 pr-3 text-paper-100">{r.master}</td>
        <td className="py-1.5 pr-3 text-muted">{r.touch}</td>
        <td className="py-1.5 px-3 text-right tabular-nums">{fmt(sent)}</td>
        <td className="py-1.5 px-3 text-right tabular-nums">{fmt(opens)}</td>
        <td
          className={
            'py-1.5 px-3 text-right tabular-nums ' +
            (anomaly ? 'text-watch font-semibold' : '')
          }
        >
          {openPct == null ? '—' : openPct.toFixed(1)}
        </td>
        <td className="py-1.5 px-3 text-right tabular-nums">{dim(r.replies)}</td>
        <td
          className={
            'py-1.5 px-3 text-right tabular-nums ' +
            (engagedHot ? 'text-ok font-semibold' : '')
          }
        >
          {dim(r.engaged)}
        </td>
        <td className="py-1.5 px-3 text-right tabular-nums">{dim(r.redirect)}</td>
        <td className="py-1.5 px-3 text-right tabular-nums">{dim(r.ga4_sessions)}</td>
        <td className="py-1.5 pl-3 text-right tabular-nums">{dim(r.ga4_leads)}</td>
      </tr>,
    );
  }
  return out;
}

function fmt(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString();
}

function dim(n: number | null | undefined): JSX.Element {
  const v = n ?? 0;
  if (v === 0) return <span className="text-muted/40">0</span>;
  return <>{v.toLocaleString()}</>;
}
